<template>
    <div class="row site-monitoring">
        <AttachmentLightbox
            v-if="lightboxImage"
            :src="lightboxImage"
            @closed="lightboxImage = null"
        />

        <MarkingDetailsModal
            :showModal="showMarking"
            :marking="marking"
            :project-tools="projectTools"
            :view-only="!canEditMarkings"
            @openLightbox="openLightbox"
            @save="saveEdits"
            @delete="deleteMarking"
            @unDelete="undeleteMarking"
            @close="marking = null; showMarking = false"
        />

        <b-modal
            id="toolColorModal"
            ref="toolColorModal"
            :title="colorPickerTool ? 'Bulk color: ' + colorPickerTool.long_name : 'Bulk color'"
            size="sm"
            hide-footer
            @hidden="pendingColor = null; pendingColorSelected = false"
        >
            <div v-if="colorPickerTool">
                <p class="mb-2 text-muted small">Select a color to apply to all <strong>{{ colorPickerTool.long_name }}</strong> markings in this file.</p>
                <div class="bulk-color-swatches">
                    <div
                        class="bulk-color-swatch bulk-color-swatch--unset"
                        :style="{ backgroundColor: colorPickerTool.color || '#000000' }"
                        :class="{ 'bulk-color-swatch--selected': pendingColorSelected && pendingColor === null }"
                        title="Default (unset — uses tool color)"
                        @click="pendingColor = null; pendingColorSelected = true"
                    ></div>
                    <div
                        v-for="colorOption in colorPickerTool.catalog_1_values || []"
                        :key="colorOption.value"
                        class="bulk-color-swatch"
                        :style="{ backgroundColor: colorOption.value }"
                        :class="{ 'bulk-color-swatch--selected': pendingColorSelected && pendingColor === colorOption.value }"
                        :title="colorOption.text"
                        @click="pendingColor = colorOption.value; pendingColorSelected = true"
                    ></div>
                </div>
            </div>
            <div v-else class="text-muted small">Tool not found.</div>

            <div class="align-right mt-3">
                <b-button variant="secondary" @click="$bvModal.hide('toolColorModal')">Cancel</b-button>
                <b-button
                    variant="primary"
                    class="ml-2"
                    :disabled="!pendingColorSelected || isBulkUpdatingColor"
                    @click="confirmBulkColor(pendingColor)"
                >
                    <b-spinner small v-if="isBulkUpdatingColor"></b-spinner>
                    OK
                </b-button>
            </div>
        </b-modal>

        <b-modal
            id="reportModal"
            ref="reportModal"
            title="Create report"
            size="lg"
            ok-title="Create"
            :ok-disabled="reportMarkingCount.selected === 0"
            @show="resetReportModal"
            @ok="handleReportModalOk"
        >
            <p v-if="!activeUser">This action will create a new report with all the markings. Please complete the
                headline of the newly created report below.</p>
            <p v-else>This action will create a new report containing only the markings created by <b
                v-text="activeUser.name"></b>. Please complete the headline of the newly created report below.</p>
            <form ref="reportTitleForm" @submit.stop.prevent="createReport">
                <b-form-group
                    label="Report headline"
                    label-for="name-input"
                    invalid-feedback="Headline is required"
                    :state="reportTitleState"
                >
                    <b-form-input
                        id="name-input"
                        v-model="reportTitle"
                        :state="reportTitleState"
                        required
                    ></b-form-input>
                </b-form-group>

                <div class="mt-2">
                    <a href="#"
                       class="text-muted small d-flex align-items-center"
                       @click.prevent="reportModalAdvanced = !reportModalAdvanced">
                        <b-icon :icon="reportModalAdvanced ? 'chevron-down' : 'chevron-right'" class="mr-1"></b-icon>
                        Advanced options
                    </a>
                    <b-collapse v-model="reportModalAdvanced">
                        <p class="mt-3 mb-2 small text-muted">
                            <strong>{{ reportMarkingCount.selected }}</strong> of {{ reportMarkingCount.total }} markings selected
                        </p>
                        <div style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
                        <div class="row">
                            <div v-if="reportColorOptions.length > 1" class="col-3">
                                <p class="mb-1 small font-weight-bold">By color:</p>
                                <ul class="list-unstyled mb-0">
                                    <li v-for="opt in reportColorOptions"
                                        :key="opt.value ?? '__none__'"
                                        class="d-flex align-items-center report-modal-type-item">
                                        <b-form-checkbox
                                            v-model="reportColorFilter"
                                            :value="opt.value"
                                            class="mr-2 mb-0"
                                            variant="danger"
                                            @click.native.stop=""
                                        ></b-form-checkbox>
                                        <span
                                            class="report-color-swatch mr-2"
                                            :style="{ background: opt.value || '#000000' }"
                                        ></span>
                                        <span class="report-type-label">{{ opt.label }}</span>
                                    </li>
                                </ul>
                            </div>
                            <div class="col-3">
                                <p class="mb-1 small font-weight-bold">By type:</p>
                                <ul class="list-unstyled mb-0">
                                    <li v-for="opt in reportTypeOptions" :key="opt.value"
                                        class="d-flex align-items-center report-modal-type-item">
                                        <b-form-checkbox
                                            v-model="reportTypeFilter"
                                            :value="opt.value"
                                            class="mr-2 mb-0"
                                            variant="danger"
                                            @click.native.stop=""
                                        ></b-form-checkbox>
                                        <template v-if="opt.kind === 'tool' && opt.tool">
                                            <CustomMarkingIcon
                                                v-if="opt.type === 'firestop'"
                                                class="report-modal-tool-icon"
                                                :marking-color="opt.tool.color || '#000000'"
                                                :middle-text="opt.tool.short_name || ''"
                                                :top-text="null"
                                                :bottom-text="null"
                                            ></CustomMarkingIcon>
                                            <FirestopMarkingIcon
                                                v-else-if="opt.type === 'custom'"
                                                class="report-modal-tool-icon"
                                                :marking-color="opt.tool.color || '#000000'"
                                                :middle-text="opt.tool.short_name || ''"
                                                :top-text="null"
                                                :bottom-text="null"
                                            ></FirestopMarkingIcon>
                                        </template>
                                        <span v-else class="report-type-icon" :class="'type-icon--' + opt.value"></span>
                                        <span class="report-type-label">{{ opt.label }}</span>
                                    </li>
                                </ul>
                            </div>
                            <div v-if="reportDateMin" class="col-3">
                                <div class="d-flex align-items-center report-modal-type-item mt-n2">
                                    <b-form-checkbox
                                        v-model="reportDateEnabled"
                                        class="mr-2 mb-0"
                                        variant="danger"
                                        @click.native.stop=""
                                    ></b-form-checkbox>
                                    <span class="report-type-label font-weight-bold small">Filter by date</span>
                                </div>
                                <template v-if="reportDateEnabled">
                                    <div class="d-flex mt-2 mb-2 report-date-radios" style="gap: 0.75rem;">
                                        <b-form-radio v-model="reportDateField" value="updated" size="sm">Updated</b-form-radio>
                                        <b-form-radio v-model="reportDateField" value="created" size="sm">Created</b-form-radio>
                                    </div>
                                    <date-range-slider
                                        :min="reportDateMin"
                                        :max="reportDateMax"
                                        :from.sync="reportDateFrom"
                                        :to.sync="reportDateTo"
                                    ></date-range-slider>
                                    <div>
                                        <label class="small mb-0 text-muted">From</label>
                                        <b-form-input
                                            type="date"
                                            v-model="reportDateFrom"
                                            :min="reportDateMin"
                                            :max="reportDateTo"
                                            lang="fi"
                                            size="sm"
                                        ></b-form-input>
                                    </div>
                                    <div class="mt-2">
                                        <label class="small mb-0 text-muted">To</label>
                                        <b-form-input
                                            type="date"
                                            v-model="reportDateTo"
                                            :min="reportDateFrom"
                                            :max="reportDateMax"
                                            lang="fi"
                                            size="sm"
                                        ></b-form-input>
                                    </div>
                                </template>
                            </div>
                            <div v-if="reportUserOptions.length > 1" class="col-3">
                                <p class="mb-1 small font-weight-bold">By user:</p>
                                <ul class="list-unstyled mb-0">
                                    <li v-for="opt in reportUserOptions" :key="opt.value"
                                        class="d-flex align-items-center report-modal-type-item">
                                        <b-form-checkbox
                                            v-model="reportUserFilter"
                                            :value="opt.value"
                                            class="mr-2 mb-0"
                                            variant="danger"
                                            @click.native.stop=""
                                        ></b-form-checkbox>
                                        <b-icon icon="person-fill" class="mr-2 flex-shrink-0" style="width:17px;height:17px;color:#555;"></b-icon>
                                        <span class="report-type-label">{{ opt.label }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        </div>
                    </b-collapse>
                </div>
            </form>
        </b-modal>

        <div class="col-sm-3 users-monitoring full-inner-page pt-2">
            <template v-for="file in files">

                <div class="folder" v-if="file.is_folder" @click.exact="openFolder(file)">
                    {{ file.name }}
                </div>

                <div v-if="selectedFolder !== null && selectedFolder.id === file.id" class="subFiles"
                     v-for="child in file.children">
                    <router-link tag="div" :key="child.id"
                                 :class="'file ' + (child.has_markings ? 'has_markings' : '')"
                                 :to="{ name: 'projects-markings-view', params: { id: $route.params.id, file_id: child.id }}"
                                 v-text="child.name"></router-link>

                    <ul class="list-unstyled padding0 margin0" v-if="activeFile && activeFile.id === child.id">
                        <router-link tag="li" v-for="user in users" :key="user.id"
                                     v-if="userHasFileMarkings(user, child)"
                                     :to="{ name: 'projects-markings-users-view', params: { id: $route.params.id, file_id: child.id, user_id: user.id }}"
                                     :class="{ 'active': activeUser && user.id === activeUser.id && child.id === activeFile.id }"
                                     v-text="user.name + (user.unique_id ? ' (' + user.unique_id + ')' : '')"
                                     exact></router-link>
                    </ul>
                </div>

                <router-link tag="div" :key="file.id" :class="'file ' + (file.has_markings ? 'has_markings' : '')"
                             v-if="!file.is_folder"
                             :to="{ name: 'projects-markings-view', params: { id: $route.params.id, file_id: file.id }}"
                             v-text="file.name" exact></router-link>

                <ul class="list-unstyled padding0 margin0" v-if="activeFile && activeFile.id === file.id">
                    <template v-for="user in users" v-if="userHasFileMarkings(user, file)">
                        <router-link tag="li"
                                     :class="{ 'active': activeUser && user.id === activeUser.id && activeFile && file.id === activeFile.id }"
                                     :to="{ name: 'projects-markings-users-view', params: { id: $route.params.id, file_id: file.id, user_id: user.id }}"
                                     exact>
                            <div class="d-flex justify-content-between align-items-center">
                                <span>{{ user.name + (user.unique_id ? ' (' + user.unique_id + ')' : '') }}</span>
<!--                            TODO: Have this here for debug purposes.
                                <div class="ml-auto"
                                     v-if="hasManagerPermission('site_monitoring.lock')">
                                    <b-icon
                                        @click.stop.prevent.exact="hasUserUnlockedMarkings(user) ? changeUserMarkingLockState(user.id, false) : null"
                                        v-b-tooltip.hover title="Unlock all markings for this user"
                                        icon="unlock-fill" font-scale="1.5" class="rounded p-1 mr-1"
                                        :class="{
                                            'bg-danger': hasUserUnlockedMarkings(user),
                                            'bg-secondary': !hasUserUnlockedMarkings(user)
                                        }"
                                        variant="light"></b-icon>
                                    <b-icon
                                        @click.stop.prevent.exact="hasUserLockedMarkings(user) ? changeUserMarkingLockState(user.id, true) : null"
                                        v-b-tooltip.hover title="Lock all markings for this user"
                                        icon="lock-fill" font-scale="1.5" class="rounded p-1 mr-1"
                                        :class="{
                                            'bg-danger': hasUserLockedMarkings(user),
                                            'bg-secondary': !hasUserLockedMarkings(user)
                                        }"
                                        variant="light"></b-icon>
                                </div>-->
                            </div>
                        </router-link>
                    </template>
                </ul>
            </template>
        </div>

        <template v-if="activeFile">
            <div ref="previewContainer"
                 :class="{'col': !activeMarkings || !activeMarkings.length, 'col-sm-6': activeMarkings && activeMarkings.length}"
                 class="full-inner-page site-monitoring-preview">
                <EmptyMarkings v-if="!pages || pages.length === 0"></EmptyMarkings>
                <PdfDocumentMobile
                    v-else
                    id="pdfPages"
                    ref="pdfDoc"
                    :active-tool="activeTool"
                    :active-project-tool="activeProjectTool"
                    :file="activeFile"
                    :pages="pages"
                    :markings="activeMarkings"
                    :selectedMarking="marking"
                    :editMode="canEditMarkings"
                    :showEditor="showEditor"
                    :showDeleted="canEditMarkings && showDeletedMarkings"
                    :paginateToPage="activePage"
                    :fileID="activeFile.id"
                    @page-changed="activePage = $event"
                    @open-page="openPage = $event"
                    @markingClick="viewMarking"
                    @editMarking="viewMarking"
                    @restoreMarking="restoreMarking"
                    :hiddenMarkingTypes="[]"
                    :projectTools="projectTools"
                    :markingSize="markingSize"
                    :markingColor="selectedColor"
                    :moveMode="moveMode"
                    @resetSelectedMarking="marking = null"
                    @toggleMoveMode="toggleMoveMode"
                    @temporaryMarkingChange="temporaryMarkingChange"
                    @markingChanged="temporaryMarkingChange"
                    :showMarkingDelete="false"
                    :showLockIcon="!isPrinting"
                ></PdfDocumentMobile>
            </div>
            <div v-if="activeMarkings && activeMarkings.length" class="col-sm-3 full-inner-page markings pt-2">
                <button class="btn refresh" @click="refresh">SYNCHRONIZE
                    <b-spinner small v-if="isSyncing" variant="danger"></b-spinner>
                </button>

                <hr class="separator"/>

                <button
                    v-if="hasManagerPermission('site_monitoring.create_report')"
                    :disabled="isCreatingReport"
                    v-b-modal.reportModal
                    @click="reportType = 0"
                    class="btn edit"> Create full report
                    <b-icon-info-circle-fill
                        v-b-tooltip
                        class="ml-1 text-muted"
                        title="A report with attachments, and all layout data will be generated."/>
                    <b-spinner small v-if="isCreatingReport && reportType === 0" variant="danger"></b-spinner>
                </button>

                <button
                    v-if="hasManagerPermission('site_monitoring.create_report')"
                    :disabled=" !markings.some(marking => marking.attachments && marking.attachments.length > 0) || isCreatingReport"
                    v-b-modal.reportModal
                    @click="reportType = 1"
                    class="btn edit report"> Create Attachments only report
                    <b-icon-info-circle-fill
                        v-b-tooltip
                        class="ml-1 text-muted"
                        title="A report with only attachment images will be generated."/>
                    <b-spinner small v-if="isCreatingReport && reportType === 1" variant="danger"></b-spinner>
                </button>

                <!--                Create layout only report
                                <button
                                    :disabled="isCreatingReport || !hasManagerPermission('site_monitoring.create_report')"
                                    v-b-modal.reportModal
                                    @click="reportType = 2"
                                    class="btn edit report"> Create Layout only report
                                    <b-icon-info-circle-fill
                                        v-b-tooltip
                                        class="ml-1 text-muted"
                                        title="A report with only layout data and markings will be generated."/>
                                    <b-spinner small v-if="isCreatingReport" variant="danger"></b-spinner>
                                </button>-->

                <!--                <button :disabled="isPrinting" class="btn print" @click="print">
                                    Print current view
                                    <b-spinner small v-if="isPrinting" variant="danger"></b-spinner>
                                </button>-->


                <hr class="separator"
                    v-if="hasManagerPermission('site_monitoring.create_report')"
                />


                <button class="btn print"
                        v-if="hasManagerPermission('site_monitoring.print')"
                        @click="openPrintPopup">Print preview
                </button>
                <PrintPopup ref="printPopup">
                    <Print
                        :markings="clonedMarkings"
                        :projectTools="projectTools"
                        :page="pages[openPage-1]"
                        :openPage="openPage"
                        :file="activeFile"
                    ></Print>
                </PrintPopup>
                <hr class="separator"/>

                <button class="btn excel" @click="excelExport">
                    Excel export
                    <b-icon-info-circle-fill
                        v-b-tooltip
                        class="ml-1 text-muted"
                        title="Microsoft Excel or compatible software is required to view the file."/>
                    <b-spinner small v-if="isExcelExporting" variant="danger"></b-spinner>
                </button>

                <div class="markings-inner">
                    <div class="markings-filter p-1 mb-1 d-flex align-items-center justify-content-between">
                        <label v-if="canEditMarkings" class="checkbox-label d-flex align-items-center mb-0 text-secondary">
                            <b-icon icon="trash"></b-icon>
                            Show deleted
                            <input type="checkbox" v-model="showDeletedMarkings" @click.stop>
                            <span class="custom-checkbox"></span>
                        </label>
                        <label class="d-flex text-nowrap align-items-center mb-0 text-secondary">
                            Sort by:
                        <select v-model="markingSortOrder" class="markings-sort-select ml-1">
                            <option value="index">running number</option>
                            <option value="color">color</option>
                            <option value="updated">date updated</option>
                            <option value="created">date created</option>
                        </select>
                        </label>
                    </div>

                    <div v-for="(type, typeIndex) in typeNames"
                         v-if="markingsByType(type).length"
                         class="accordion"
                         :key="`type-${typeIndex}`">
                        <div class="card">
                            <div class="card-header" :id="`collapse-${type}-${typeIndex}-header`">
                                <h3 class="mb-0">
                                    <button class="btn btn-link"
                                            type="button"
                                            :data-target="`collapse-${type}-${typeIndex}`"
                                            v-b-toggle="`collapse-${type}-${typeIndex}`"
                                            :aria-controls="`collapse-${type}-${typeIndex}`"
                                            aria-expanded="true"
                                            @click.stop>
                                        <span class="collapse-header-title" v-text="types[type]"></span>
                                    </button>
                                </h3>
                            </div>

                            <b-collapse :id="`collapse-${type}-${typeIndex}`"
                                        :aria-labelledby="`collapse-${type}-${typeIndex}-header`"
                                        visible>
                                <b-card>

                                    <template v-if="type === 'firestop' || type === 'custom'">
                                        <div v-for="(titleGroup, title, titleIndex) in groupMarkingsByTitle(type)"
                                             :key="`title-${type}-${titleIndex}`"
                                             class="accordion">
                                            <div class="card">
                                                <div class="card-header" :id="`collapse-${type}-${titleIndex}-header`">
                                                    <h4 class="mb-0 d-flex align-items-center">
                                                        <button class="btn btn-link flex-grow-1 text-left"
                                                                type="button"
                                                                :data-target="`collapse-${type}-${titleIndex}`"
                                                                v-b-toggle="`collapse-${type}-${titleIndex}`"
                                                                :aria-controls="`collapse-${type}-${titleIndex}`"
                                                                @click.stop>
                                                            <span class="collapse-header-title"
                                                                  v-text="title || 'Untitled'"></span>
                                                        </button>
                                                        <button
                                                            v-if="canEditMarkings && toolForGroup(titleGroup)"
                                                            class="btn btn-sm tool-color-icon-btn"
                                                            :title="'Bulk color: ' + toolForGroup(titleGroup).long_name"
                                                            @click.stop="openColorPicker(toolForGroup(titleGroup))"
                                                        >
                                                            <b-icon icon="brush"></b-icon>
                                                        </button>
                                                    </h4>
                                                </div>

                                                <b-collapse :id="`collapse-${type}-${titleIndex}`"
                                                            :aria-labelledby="`collapse-${type}-${titleIndex}-header`"
                                                            visible>
                                                    <b-card>
                                                        <ul class="list-unstyled" :class="type + '-list'">
                                                            <li v-for="(marking, markingIndex) in titleGroup"
                                                                :key="`marking-${markingIndex}`"
                                                                @click="jumpToMarking(marking)"
                                                                @mouseenter="highLightMarking(marking.id)"
                                                                @mouseleave="clearMarkingHighlight"
                                                                class="marking-row"
                                                                :class="{ 'mod-checked': marking.is_checked, 'marking-deleted': marking.deleted_at }">
                                                                <template v-if="type === 'firestop'">
                                                                    <CustomMarkingIcon
                                                                        class="firestopMarkingIcon"
                                                                        :bottom-text="marking.tool.show_lower_detail ? markingPrefix(marking) : null"
                                                                        :marking-color="marking.catalog_1 || marking.tool.color || '#000000'"
                                                                        :middle-text="marking.markingIndex.toString()"
                                                                        :top-text="marking.tool.show_upper_detail ? marking.text_1 : null"
                                                                    ></CustomMarkingIcon>
                                                                </template>

                                                                <template v-else-if="type === 'custom'">
                                                                    <FirestopMarkingIcon
                                                                        class="firestopMarkingIcon"
                                                                        :bottom-text="marking.tool.show_lower_detail ? markingPrefix(marking) : null"
                                                                        :marking-color="marking.catalog_1 || marking.tool.color || '#000000'"
                                                                        :middle-text="marking.markingIndex.toString()"
                                                                        :top-text="marking.tool.show_upper_detail ? marking.catalog_2 : null"
                                                                    ></FirestopMarkingIcon>
                                                                </template>

                                                                <span class="marking-actions">
                                                                    <span v-if="marking.page != null" class="marking-page-icon" title="Page">
                                                                    <b-icon icon="file-earmark" font-scale="1.4"></b-icon>
                                                                    <span class="marking-page-number">{{ marking.page }}</span>
                                                                </span>

                                                                    <span @click.stop="openMarkingModal(marking)"
                                                                          :title="canEditMarkings ? 'Edit marking' : 'View marking'">
                                                                        <b-icon-pencil v-if="canEditMarkings"/>
                                                                        <b-icon-eye v-else/>
                                                                    </span>
                                                                </span>

                                                                <span v-text="markingLabel(marking)"></span>
                                                                <span v-if="marking.title"
                                                                      v-text="' - ' + marking.title"></span>
                                                            </li>
                                                        </ul>
                                                    </b-card>
                                                </b-collapse>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-else>
                                        <ul class="list-unstyled" :class="type + '-list'">
                                            <li v-for="(marking, markingIndex) in markingsByType(type)"
                                                :key="`marking-${markingIndex}`"
                                                @click="jumpToMarking(marking)"
                                                @mouseenter="highLightMarking(marking.id)"
                                                @mouseleave="clearMarkingHighlight"
                                                class="marking-row"
                                                :class="{ 'mod-checked': marking.is_checked,  'marking-deleted': marking.deleted_at }">

                                                <span class="marking-actions">
                                                <span v-if="marking.page != null" class="marking-page-icon" title="Page">
                                                    <b-icon icon="file-earmark" font-scale="1.4"></b-icon>
                                                    <span class="marking-page-number">{{ marking.page }}</span>
                                                </span>


                                                    <span @click.stop="openMarkingModal(marking)"
                                                          :title="canEditMarkings ? 'Edit marking' : 'View marking'">
                                                        <b-icon-pencil v-if="canEditMarkings"/>
                                                        <b-icon-eye v-else/>
                                                    </span>
                                                </span>
                                                <span v-text="markingLabel(marking)"></span>
                                                <span v-if="marking.title" v-text="' - ' + marking.title"></span>
                                            </li>
                                        </ul>
                                    </template>
                                </b-card>
                            </b-collapse>
                        </div>
                    </div>
                </div>

            </div>

            <div class="move-navigation" v-if="moveMode">
                <p>Moving marking</p>
                <button v-on:click="updateMarking(marking, true)">Done</button>
            </div>

            <Editor v-if="showEditor" :marking="this.marking" v-on:saveMarking="updateMarking"
                    :project-tools="projectTools"
                    saveButtonText="Save and lock"
                    :showDeleteButton="false"
                    v-on:cancelEditMarking="cancelEditMarking">
            </Editor>
        </template>
    </div>
</template>

<script>
import '@css/site-monitoring.css';
import ConfirmDialog from "@mobile/components/global/ConfirmDialog";
import {map, uniqBy, isNull, reject, sortBy, orderBy, each, filter, keys, cloneDeep} from 'lodash';
import Report from '@mobile/components/report/Report';
import PdfDocumentMobile from "@mobile/components/edit_mobile/PdfDocumentMobile";
import EventBus from "@mobile/utils/event-bus";
import Comments from "@mobile/components/edit/Comments";
import Editor from '@mobile/components/edit/Editor';
import PrintPopup from '@components/common/PopupModal.vue';
import Print from './print/Print.vue'
import html2canvas from 'html2canvas';
import CustomMarkingIcon from "@mobile/components/global/Markings/CustomMarkingIcon";
import FirestopMarkingIcon from "@mobile/components/global/Markings/FirestopMarkingIcon";
import axios from "axios";
import EmptyMarkings from "@components/markings/EmptyMarkings.vue";
import DateRangeSlider from "@components/common/DateRangeSlider.vue";
import MarkingDetailsModal from './editor/MarkingDetailsModal.vue';
import AttachmentLightbox from './AttachmentLightbox.vue'

export default {

    components: {
        EmptyMarkings, ConfirmDialog, AttachmentLightbox, MarkingDetailsModal, DateRangeSlider,
        Print, PrintPopup, Report, PdfDocumentMobile, Comments, CustomMarkingIcon, FirestopMarkingIcon, Editor
    },
    data() {
        return {
            showDeletedMarkings: localStorage.getItem('showDeletedMarkings') === 'true',
            showMarking: false,
            changeLogs: [],
            lightboxImage: null,
            activeTab: 'active',
            deleteAttachmentConfirmIsVisible: false,
            lastHoveredEl: null,
            showMarkingModal: false,
            showMobile: false,
            changelogs: [],
            mergedChangeLogKeys: {},
            reportType: 0, // 0-full, 1-dummy 2-full
            files: [],
            markings: [],
            clonedMarkings: [],
            project: null,
            activeFile: null,
            pages: [],
            activeUser: null,
            activePage: 1,
            openPage: 1,
            savedPage: null,
            types: {
                camera: "Camera",
                measure: "Measurements",
                gallery: "Gallery",
                note: "Notes",
                draw: "Drawings",
                checkbox: "Checkbox",
                firestop: "Firestop",
                custom: "Custom"
            },
            marking: null,
            reportTitle: '',
            reportTitleState: null,
            reportModalAdvanced: true,
            reportDateEnabled: false,
            reportDateField: 'updated',
            reportColorFilter: [],
            reportTypeFilter: [],
            reportTypeOptions: [],
            reportColorOptions: [],
            reportUserFilter: [],
            reportUserOptions: [],
            reportDateFrom: null,
            reportDateTo: null,
            reportDateMin: null,
            reportDateMax: null,
            isCreatingReport: false,
            isSyncing: false,
            selectedFolder: null,
            projectTools: [],

            isPrinting: false,
            isExcelExporting: false,

            showEditor: false,
            moveMode: 0,
            selectedColor: '#000000',
            activeProjectTool: null,
            temporaryMarking: null,
            currentMarkingIndex: null,

            markingSize: 'large',

            colorPickerTool: null,
            pendingColor: null,
            pendingColorSelected: false,
            isBulkUpdatingColor: false,

            markingSortOrder: 'index',
        }
    },

    watch: {
        showDeletedMarkings(val) {
            localStorage.setItem('showDeletedMarkings', val);
        },
        'project.id': function () {
            this.loadFiles();
            //TODO: Do we even need this?
            // this.loadMarkings();
            this.loadProjectTools();
        },

        showMarking: function () {
            //console.log(this.showMarking);
        },

        activeFile: function () {
            this.activeUser = null;
            this.activePage = 1;
            this.savedPage = null;
        },

        reportDateField() {
            this.updateReportDates();
        },
    },

    computed: {
        markingPreviewComponent() {
            if (!this.marking?.type) return null

            switch (this.marking.type) {
                case 'firestop':
                    return FirestopMarkingIcon
                case 'custom':
                    return CustomMarkingIcon
                default:
                    return null
            }
        },

        hasHitAttachmentLimits() {
            const trashedCount = (this.marking?.attachments ?? [])
                .filter(attachment => attachment.is_trashed === false)
                .length

            return trashedCount > 5
        },

        hasActiveAttachments(marking) {
            return (
                    this.marking &&
                    this.marking.attachments) &&
                this.marking.attachments.some(a => !a.is_trashed)
        },

        hasTrashedAttachments() {
            return (
                this.marking &&
                this.marking.attachments &&
                this.marking.attachments.some(a => a.is_trashed)
            )
        },

        tabHeadline() {
            switch (this.activeTab) {
                case 'active':
                    return 'Active Marking'
                case 'content':
                    return 'Edit Marking Content'
                case 'selected-pictures':
                    return 'Selected Picture Attachments'
                case 'pending-pictures':
                    return 'Pending Picture Attachments'
                case 'trashed-pictures':
                    return 'Trashed Picture Attachments'
                case 'changelog':
                    return 'Marking Changelog'
                default:
                    return 'Active Marking'
            }
        },

        markingModalTitle: function () {
            if (!this.marking) {
                return;
            }
            let title = this.markingLabel(this.marking);

            if (this.marking.title) {
                title += ' - ' + this.marking.title;
            }
            return title;
        },

        users() {
            return sortBy(
                uniqBy(
                    reject(
                        map(this.markings, (report) => report.created_by),
                        isNull
                    ),
                    'id'
                ),
                ['name'],
                ['asc']
            );
        },

        activeMarkings() {
            const filtered = filter(this.markings, (marking) => {
                return (!this.activeFile || marking.file_id === this.activeFile.id) &&
                    (!this.activeUser || marking.created_by.id === this.activeUser.id) &&
                    ((this.canEditMarkings && this.showDeletedMarkings) || !marking.deleted_at);
            });

            if (this.markingSortOrder === 'color') {
                return sortBy(filtered, marking => {
                    if (!marking.catalog_1) return 0; // unset/default first
                    const toolValues = (marking.tool && marking.tool.catalog_1_values) || [];
                    const idx = toolValues.findIndex(v => v.value === marking.catalog_1);
                    return idx === -1 ? 999 : idx + 1; // 1-based so 0 (default) stays first
                });
            }

            if (this.markingSortOrder === 'updated') {
                return orderBy(filtered, ['updated_at'], ['desc']);
            }

            if (this.markingSortOrder === 'created') {
                return orderBy(filtered, ['created_at'], ['desc']);
            }

            return filtered;
        },
        reportMarkingCount() {
            if (!this.activeFile) return { selected: 0, total: 0 };
            const fileMarkings = this.markings.filter(m =>
                m.file_id === this.activeFile.id && !m.deleted_at
            );
            const total = fileMarkings.length;
            const allColorsSelected = this.reportColorOptions.length === 0 ||
                this.reportColorOptions.every(o => this.reportColorFilter.includes(o.value));
            const allTypesSelected = this.reportTypeOptions.every(o => this.reportTypeFilter.includes(o.value));
            const allUsersSelected = this.reportUserOptions.every(o => this.reportUserFilter.includes(o.value));
            const allDatesSelected = !this.reportDateEnabled || (this.reportDateFrom === this.reportDateMin && this.reportDateTo === this.reportDateMax);
            if (allColorsSelected && allTypesSelected && allUsersSelected && allDatesSelected) return { selected: total, total };
            const dateKey = this.reportDateField === 'updated' ? 'updated_at' : 'created_at';
            const selected = fileMarkings.filter(m => {
                if (!allUsersSelected && !(m.created_by && this.reportUserFilter.includes(m.created_by.id))) return false;
                if (!allDatesSelected && m[dateKey]) {
                    const d = m[dateKey].substring(0, 10);
                    if (d < this.reportDateFrom || d > this.reportDateTo) return false;
                }
                if (!allTypesSelected) {
                    const isTool = m.type === 'custom' || m.type === 'firestop';
                    if (isTool) {
                        if (!this.reportTypeFilter.includes(m.project_marking_tool_id)) return false;
                    } else {
                        if (!this.reportTypeFilter.includes(m.type)) return false;
                    }
                }
                if (!allColorsSelected && (m.type === 'custom' || m.type === 'firestop')) {
                    return this.reportColorFilter.includes(m.catalog_1);
                }
                return true;
            }).length;
            return { selected, total };
        },

        activeTool() {
            return null;
        },
        // False for view-only roles (Outside inspector): they may look at markings
        // but not edit, move, delete/restore or see changelog data.
        canEditMarkings() {
            return !!this.hasManagerPermission('site_monitoring.edit');
        },
        typeNames: function () {
            return keys(this.types);
        },

    },

    beforeRouteEnter: function (to, from, next) {
        next(function (vm) {
            vm.updateActiveFile();
            vm.updateActiveUser();
        });
    },

    beforeRouteUpdate: function (to, from, next) {
        next();
        this.updateActiveFile();
        this.updateActiveUser();
    },

    mounted() {
        this.project = this.$store.state.project;
    },

    methods: {
        openLightbox(attachmentUrl) {
            console.log(attachmentUrl);
            this.lightboxImage = attachmentUrl;
            this.$nextTick(() => {
                this.$bvModal.show('attachment-lightbox')
            })
        },

        fetchChangeLogs() {
            this.$store.dispatch('APIgetMarkingChangelogAttachments', {
                file_id: this.marking.file_id,
                marking_id: this.marking.id
            }).then(response => {
                this.changeLogs = response || [];
            }).catch(error => {
                console.error('Error fetching change logs:', error);
                this.changeLogs = [];
            });
        },

        saveEdits(marking, silent = false) {
            if (!this.canEditMarkings) {
                return;
            }

            this.updateMarking(marking, true, silent);
            this.unsavedChanges = false;
        },

        setTab(tab) {
            this.activeTab = tab
        },

        isActive(tab) {
            return this.activeTab === tab
        },

        isParentActive(tabs) {
            return tabs.includes(this.activeTab)
        },

        mergeChangedKeys() {
            if (!Array.isArray(this.changelogs) || this.changelogs.length === 0) {
                this.mergedChangeLogKeys = {};
                return;
            }

            this.mergedChangeLogKeys = this.changelogs.reduce((acc, log) => {
                return {
                    ...acc,
                    ...(log.changed_keys || {}),
                };
            }, {});
        },

        viewMobile(marking) {
            this.activeTab = 'mobile';

            const token = this.$store.state.token;

            axios.get(this.$store.getters.mangerURL + '/api/change-logs', {
                params: {marking_id: marking.id},
                headers: {
                    "Authorization": "Bearer " + token
                }
            })
                .then(response => {
                    this.changelogs = response.data || [];
                    this.mergeChangedKeys();
                }).catch(err => {
                console.error(err);
            });

        },

        openPrintPopup() {
            this.clonedMarkings = cloneDeep(this.activeMarkings).filter(m => !m.deleted_at);

            this.$refs.printPopup.openPopup();
        },

        openMarkingModal(marking) {
            const index = this.activeMarkings.findIndex(m => m.id === marking.id);
            this.viewMarking(index);
        },

        hideEditor() {
            this.showEditor = false;
        },

        toggleMoveMode(moveMode) {
            this.moveMode = (moveMode && this.canEditMarkings) ? 1 : 0;
            this.$forceUpdate();
        },

        markingIndex(marking) {
            let index = marking.markingIndex;
            if (marking.created_by && marking.created_by.unique_id && ('' + marking.created_by.unique_id).length) {
                index += ':' + marking.created_by.unique_id;
            } else if (this.$store.state.company && this.$store.state.company.unique_user_id) {
                index += ':' + this.$store.state.company.unique_user_id;
            }

            return index;
        },
        markingPrefix(marking) {
            if (marking.tool && (marking.type === 'custom' || marking.type === 'firestop')) {
                if (marking.tool && marking.tool.short_name) {
                    return marking.tool.short_name;
                } else if (marking.project_marking_tool_id) {
                    const tool = this.projectTools.find(tool => tool.id === marking.project_marking_tool_id);
                    if (tool) {
                        return tool.short_name;
                    }
                }
            }

            if (marking.type === 'checkbox') {
                if (marking.is_checked) {
                    return 'OK';
                }
                return 'NOK';
            }

            return marking.markingPrefix;
        },

        restorePage: function () {
            let page = this.savedPage;

            this.savedPage = null;

            if (page) {
                this.$refs.pdf.setPage(page);
            }
        },

        refresh: async function () {
            this.isSyncing = true;
            this.activeFile = null;
            this.updateActiveFile();
            this.isSyncing = false;
        },

        loadFiles() {
            if (!this.$store.state.project.id) {
                return;
            }
            this.$store.dispatch('loadFiles').then((files) => {
                this.files = files;
                this.updateActiveFile();
                this.isSyncing = false;
            });
        },

        async loadMarkings() {
            let fileID = this.activeFile.id;

            return this.$store.dispatch('loadMarkingsForFile', fileID).then((markings) => {
                this.markings = [];
                Object.assign(this.markings, markings);
                this.isSyncing = false;
            }).catch(error => {
                console.error("Error loading markings:", error);
                this.isSyncing = false;
                return [];
            });
        },


        updateActiveUser() {
            let userId = this.$route.params.user_id;

            if (this.activeUser && this.activeUser.id === userId) {
                return;
            }
            this.activeUser = null;

            if (!userId) {
                return;
            }
            each(this.markings, (marking) => {
                if (marking.created_by.id === userId) {
                    this.$nextTick(() => {
                        this.activeUser = marking.created_by;
                    });
                }
            });
        },

        updateActiveFile() {
            let fileId = this.$route.params.file_id;

            if (this.activeFile && this.activeFile.id === fileId) {
                return;
            }
            this.activeFile = null;

            if (!fileId) {
                return;
            }
            this.isCreatingReport = true;

            this.$store.dispatch('APIgetFileWithMarkings', {
                file_id: fileId,
            }).then((data) => {
                let file = {
                    id: data.file.file.id,
                    project_id: data.file.project_id,
                    name: data.file.file.name,
                    url: data.file.file.url
                };
                let pages = data.file.pages;
                let markings = [];

                if (data.markings && data.markings.length > 0) {

                    data.markings.forEach(marking => {
                        marking.attachments = [];
                        if (marking.groupped_attachments && marking.groupped_attachments.length > 0) {
                            marking.groupped_attachments.forEach(attachments => {
                                if (attachments.length > 0) {
                                    attachments.forEach(attachment => {
                                        marking.attachments.push(attachment);
                                    });
                                }

                            });
                            delete marking.groupped_attachments;
                        }
                        marking.file_id = file.id;
                        marking.is_collaborate = marking.is_collaborative;
                        markings.push(marking);
                    });
                }

                this.pages = pages;
                this.markings = markings;
                this.activeFile = file;
                this.isCreatingReport = false;
                this.$forceUpdate();

            }).catch(err => {
                this.isCreatingReport = false;
            });
        },

        changeUserMarkingLockState(userId, lock) {

            this.$bvModal.msgBoxConfirm('Are you sure you want to ' + (lock ? 'lock' : 'unlock') + ' all markings for this user?', {
                title: 'Confirm action',
                size: 'md',
                buttonSize: 'md',
                okVariant: 'danger',
                okTitle: (lock ? 'Lock' : 'Unlock'),
                cancelTitle: 'Cancel',
                centered: true
            }).then(res => {
                if (res) {
                    this.isCreatingReport = true;
                    const token = this.$store.state.token;

                    const url = this.$store.getters.apiProjectBase + '/files/' + this.activeFile.id + (lock ? '/lock-user-markings/' : '/unlock-user-markings/') + userId;

                    axios({
                        url: url,
                        method: 'GET',
                        headers: {
                            "Authorization": "Bearer " + token
                        }
                    }).then(() => {
                        this.isCreatingReport = false;
                        this.activeFile = null;
                        this.updateActiveFile();
                    }).catch(err => {
                        this.isCreatingReport = false;
                        console.error(err, 'error when trying to lock user markings');
                    });
                }
            });

        },

        markingsByType(type) {
            if (!this.activeFile) {
                return [];
            }
            return filter(this.activeMarkings, (marking) => {
                return this.activeFile.id === marking.file_id && marking.type === type;
            });

        },

        groupMarkingsByTitle(type) {
            const markings = this.markingsByType(type);
            return markings.reduce((groups, marking) => {
                const title = marking.title || '';
                if (!groups[title]) {
                    groups[title] = [];
                }
                groups[title].push(marking);
                return groups;
            }, {});
        },

        jumpToMarking(marking) {
            const pdf = this.$refs.pdfDoc;
            if (!pdf || typeof pdf.jumpToMarking !== 'function') {
                console.warn('PdfDocumentMobile ref not ready or jumpToMarking() not found');
                return;
            }
            pdf.jumpToMarking(marking, 0.4);
        },

        highLightMarking(markingIndex, marking) {
            if (this.lastHoveredEl) {
                this.lastHoveredEl.classList.remove('marking-highlighted');
            }

            const el = document.querySelector(`.marking-wrap[data-marking-index="${markingIndex}"]`);
            if (el) {
                el.classList.add('marking-highlighted');
                this.lastHoveredEl = el;
            }
        },

        clearMarkingHighlight() {
            if (this.lastHoveredEl) {
                this.lastHoveredEl.classList.remove('marking-highlighted');
                this.lastHoveredEl = null;
            }
        },


        userHasFileMarkings(user, file) {
            let has = false;

            each(this.markings, function (marking) {
                if (marking.file_id === file.id && marking.created_by.id === user.id) {
                    has = true;
                }
            });

            return has;
        },

        markingLabel(marking) {
            let label;

            if (marking.type !== 'checkbox') {
                label = marking.type[0].toUpperCase();
            } else {
                label = (marking.is_checked ? 'OK' : 'NOK');
            }
            label = marking.markingPrefix + '_' + marking.markingIndex;

            if (marking.created_by && ('' + marking.created_by.unique_id).length) {
                label += ':' + marking.created_by.unique_id;
            }
            return label;
        },

        onHideMarkingModal() {
            this.activeTab = 'active'
            this.changelogs = [];
            this.mergedChangeLogKeys = {};
        },

        onCloseMarkingModal() {
            this.$refs.markingModal.hide();

            setTimeout(() => {
                this.marking = null;
            }, 300);
        },

        viewMarking(markingIndex) {
            const marking = this.activeMarkings[markingIndex];

            if (marking) {
                this.currentMarkingIndex = markingIndex;
                this.marking = cloneDeep(marking);
                this.showMarking = true;
            }
        },

        checkReportFormValidity() {
            const valid = this.$refs.reportTitleForm.checkValidity()
            this.reportTitleState = valid;
            return valid;
        },
        handleReportModalOk(bvModalEvt) {
            // Prevent modal from closing
            bvModalEvt.preventDefault()
            // Trigger submit handler
            this.createReport()
        },
        buildReportTypeOptions() {
            if (!this.activeFile) return [];
            const simpleOrder = [
                { value: 'camera',   label: 'Camera',       kind: 'type' },
                { value: 'note',     label: 'Notes',         kind: 'type' },
                { value: 'measure',  label: 'Measurements',  kind: 'type' },
                { value: 'gallery',  label: 'Gallery',        kind: 'type' },
                { value: 'draw',     label: 'Drawings',       kind: 'type' },
                { value: 'checkbox', label: 'Checkbox',       kind: 'type' },
            ];
            const fileMarkings = this.markings.filter(m =>
                m.file_id === this.activeFile.id && !m.deleted_at
            );
            const presentSimpleTypes = new Set(
                fileMarkings.filter(m => m.type !== 'custom' && m.type !== 'firestop').map(m => m.type)
            );
            const simple = simpleOrder.filter(o => presentSimpleTypes.has(o.value));
            const seenTools = new Set();
            const tools = [];
            for (const m of fileMarkings) {
                if (m.type !== 'custom' && m.type !== 'firestop') continue;
                if (!m.project_marking_tool_id || seenTools.has(m.project_marking_tool_id)) continue;
                seenTools.add(m.project_marking_tool_id);
                tools.push({
                    value: m.project_marking_tool_id,
                    label: (m.tool && (m.tool.long_name || m.tool.short_name)) || m.type,
                    kind: 'tool',
                    type: m.type,
                    tool: m.tool,
                });
            }
            return [...simple, ...tools];
        },

        buildReportColorOptions() {
            if (!this.activeFile) return [];
            const colorMarkings = this.markings.filter(m =>
                m.file_id === this.activeFile.id &&
                !m.deleted_at &&
                (m.type === 'custom' || m.type === 'firestop')
            );
            const seen = new Set();
            const options = [];
            let hasUnset = false;
            for (const marking of colorMarkings) {
                if (!marking.catalog_1) hasUnset = true;
                const values = marking.tool && marking.tool.catalog_1_values;
                if (values && values.length) {
                    for (const v of values) {
                        if (seen.has(v.value)) continue;
                        seen.add(v.value);
                        options.push({ value: v.value, label: v.text });
                    }
                }
            }
            if (hasUnset) {
                options.unshift({ value: null, label: 'Unset' });
            }
            return options;
        },

        buildReportUserOptions() {
            if (!this.activeFile) return [];
            const fileMarkings = this.markings.filter(m =>
                m.file_id === this.activeFile.id && !m.deleted_at
            );
            const seenUsers = new Set();
            const options = [];
            for (const m of fileMarkings) {
                if (!m.created_by || seenUsers.has(m.created_by.id)) continue;
                seenUsers.add(m.created_by.id);
                const label = m.created_by.name +
                    (m.created_by.unique_id ? ' (' + m.created_by.unique_id + ')' : '');
                options.push({ value: m.created_by.id, label });
            }
            return options;
        },

        resetReportModal() {
            this.reportTitle = '';
            this.reportTitleState = null;
            this.reportModalAdvanced = true;
            this.reportDateEnabled = false;
            this.reportDateField = 'updated';
            this.reportTypeOptions = this.buildReportTypeOptions();
            this.reportColorOptions = this.buildReportColorOptions();
            this.reportUserOptions = this.buildReportUserOptions();
            this.reportColorFilter = this.reportColorOptions.map(o => o.value);
            this.reportTypeFilter = this.reportTypeOptions.map(o => o.value);
            this.reportUserFilter = this.reportUserOptions.map(o => o.value);
            this.updateReportDates();
        },

        updateReportDates() {
            if (!this.activeFile) return;
            const dateKey = this.reportDateField === 'updated' ? 'updated_at' : 'created_at';
            const dates = this.markings
                .filter(m => m.file_id === this.activeFile.id && !m.deleted_at && m[dateKey])
                .map(m => m[dateKey].substring(0, 10))
                .sort();
            this.reportDateMin = dates.length ? dates[0] : null;
            this.reportDateMax = dates.length ? dates[dates.length - 1] : null;
            this.reportDateFrom = this.reportDateMin;
            this.reportDateTo = this.reportDateMax;
        },


        transformMarking(marking) {
            marking.attachments = [];
            if (marking.groupped_attachments && marking.groupped_attachments.length > 0) {
                marking.groupped_attachments.forEach(attachments => {
                    if (attachments.length > 0) {
                        attachments.forEach(attachment => {
                            marking.attachments.push(attachment);
                        });
                    }

                });
                delete marking.groupped_attachments;
            }

            return marking;
        },

        createReport() {
            // Exit when the form isn't valid
            if (!this.checkReportFormValidity()) {
                return
            }

            EventBus.$emit('SHOW_LOADER', {
                title: "Your report is currently being generated",
                message: "As soon as it's finished you will be able to preview it. Please wait and do not leave this screen."
            });

            this.isCreatingReport = true;

            this.$nextTick(() => {
                this.$bvModal.hide('reportModal');
            });

            let token = this.$store.state.token;
            let url = this.$store.getters.apiProjectBase + '/markings/' + this.activeFile.id;
            let method = 'PUT';

            const allColorsSelected = this.reportColorOptions.length === 0 ||
                this.reportColorOptions.every(o => this.reportColorFilter.includes(o.value));
            const allTypesSelected = this.reportTypeOptions.every(o => this.reportTypeFilter.includes(o.value));
            const allUsersSelected = this.reportUserOptions.every(o => this.reportUserFilter.includes(o.value));
            const allDatesSelected = !this.reportDateEnabled || (this.reportDateFrom === this.reportDateMin && this.reportDateTo === this.reportDateMax);

            const dateKey = this.reportDateField === 'updated' ? 'updated_at' : 'created_at';
            let markingUuids = null;
            if (!allColorsSelected || !allTypesSelected || !allUsersSelected || !allDatesSelected) {
                markingUuids = this.markings
                    .filter(m => {
                        if (m.file_id !== this.activeFile.id) return false;
                        if (!allUsersSelected && !(m.created_by && this.reportUserFilter.includes(m.created_by.id))) return false;
                        if (!allDatesSelected && m[dateKey]) {
                            const d = m[dateKey].substring(0, 10);
                            if (d < this.reportDateFrom || d > this.reportDateTo) return false;
                        }
                        if (!allTypesSelected) {
                            const isTool = m.type === 'custom' || m.type === 'firestop';
                            if (isTool) {
                                if (!this.reportTypeFilter.includes(m.project_marking_tool_id)) return false;
                            } else {
                                if (!this.reportTypeFilter.includes(m.type)) return false;
                            }
                        }
                        if (!allColorsSelected && (m.type === 'custom' || m.type === 'firestop')) {
                            return this.reportColorFilter.includes(m.catalog_1);
                        }
                        return true;
                    })
                    .map(m => m.uuid);
            }

            return axios({
                url: url,
                data: {
                    title: this.reportTitle,
                    report_type: this.reportType,
                    marking_uuids: markingUuids,
                },
                method: method,
                headers: {"Authorization": "Bearer " + token}
            }).then(response => {
                this.isCreatingReport = false;
                EventBus.$emit('HIDE_LOADER');

                if (response.data?.report?.id) {
                    this.$bvModal.msgBoxConfirm('New report was created successfully.', {
                        title: 'Report created',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'danger',
                        okTitle: 'Preview new report',
                        cancelTitle: 'Stay on this page',
                        centered: true
                    }).then(res => {
                        if (res) {
                            this.noLeaveMessage = true;
                            this.$router.push({
                                name: 'projects-reports-view',
                                params: {id: this.$store.state.project.id, report_id: response.data.report.id}
                            });
                        }
                    });
                } else {
                    this.$bvModal.msgBoxOk('Failed to create report.', {
                        title: 'Error',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'danger',
                        okTitle: 'Ok',
                        centered: true
                    });

                    EventBus.$emit('HIDE_LOADER');
                }
            }).catch(err => {
                EventBus.$emit('HIDE_LOADER');
                this.isCreatingReport = false;

                this.$bvModal.msgBoxOk('Failed to create report.', {
                    title: 'Error',
                    size: 'md',
                    buttonSize: 'md',
                    okVariant: 'danger',
                    okTitle: 'Ok',
                    centered: true
                });

                console.error(err);
            });


        },
        openFolder(folder) {
            if (this.selectedFolder !== null && this.selectedFolder.id === folder.id) {
                this.selectedFolder = null;
            } else {
                this.selectedFolder = folder;
            }
        },
        loadProjectTools() {
            this.$store.dispatch('APIgetProjectTools', this.$store.state.project.id).then((tools) => {
                this.projectTools = tools;
            });
        },
        findCatalogLabelByValue(catgalogNr, value) {
            if (this.activeTool && this.activeTool['catalog_' + catgalogNr] && this.activeTool['catalog_' + catgalogNr + '_values'].length > 0) {
                const catalogItem = this.activeTool['catalog_' + catgalogNr + '_values'].find(item => {
                    // noinspection EqualityComparisonWithCoercionJS
                    return item.value == value
                });
                return catalogItem ? catalogItem.text : '-';
            }
            return '-';
        },

        async prepareMarkingAttachments(marking) {
            if (!marking || !marking.attachments || marking.attachments.length === 0) {
                return marking;
            }

            // Clone attachments so we never corrupt the live in-memory marking
            const clonedAttachments = marking.attachments.map(a => ({ ...a }));

            for (let i = 0; i < clonedAttachments.length; i++) {
                const attachment = clonedAttachments[i];
                if (attachment.attachment && attachment.attachment instanceof Blob) {
                    attachment.name = 'marking_' + marking.uuid + '_attachment_' + i + '.jpeg';
                    const reader = new FileReader();
                    reader.readAsDataURL(attachment.attachment);
                    await new Promise(resolve => {
                        reader.onload = () => {
                            // strip data:image/...;base64, prefix — backend expects raw base64
                            attachment.attachment = reader.result.split(',')[1];
                            resolve();
                        }
                    });
                }
            }

            return { ...marking, attachments: clonedAttachments };
        },

        deleteMarking(marking) {
            if (!this.canEditMarkings) {
                return;
            }

            marking.deleted_at = Date.now();
            this.updateMarking(marking, true);
        },

        undeleteMarking(marking) {
            if (!this.canEditMarkings) {
                return;
            }

            marking.deleted_at = null;
            this.updateMarking(marking, true);
        },

        restoreMarking(markingIndex) {
            if (!this.canEditMarkings) {
                return;
            }

            const marking = this.activeMarkings[markingIndex];

            if (marking) {
                marking.deleted_at = null;
                this.currentMarkingIndex = markingIndex;
                this.marking = marking;
                this.updateMarking(marking, true);
            }
        },

        async updateMarking(editMarking, lock = true, silent = false) {
            if (!this.canEditMarkings) {
                return;
            }

            if (!editMarking) {
                editMarking = this.marking;
            }

            editMarking.is_locked = lock;
            const token = this.$store.state.token;
            editMarking = await this.prepareMarkingAttachments(editMarking);

            // if(editMarking.attachments && editMarking.attachments.length > 0){
            //     editMarking.attachments.forEach((attachment, attachmentIndex) => {
            //         if(attachment.attachment && !attachment.name){
            //
            //             const attachmentName = 'marking_' + editMarking.uuid + '_' + editMarking.id + '_' + attachmentIndex;
            //             attachment.name = attachmentName;
            //             formData.append(attachmentName, attachment.attachment, attachmentName + '.jpeg');
            //         }
            //     });
            // }

            axios({
                url: this.$store.getters.apiProjectBase + '/markings/' + editMarking.id + '/update-marking',
                data: editMarking,
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + token
                }
            })
                .then(response => {
                    let newMarking = response.data.data;

                    this.markings.forEach((marking, index) => {
                        if (marking.id === newMarking.id) {
                            this.markings[index] = this.transformMarking(newMarking);

                        }
                    });

                    if (this.marking && this.marking.id === newMarking.id) {
                        this.marking = cloneDeep(newMarking);
                    }

                    this._computedWatchers.activeMarkings.run();
                    this.$forceUpdate();

                    if (!silent) {
                        let message = 'The marking was updated successfully.';
                        this.$bvModal.msgBoxOk(message, {
                            title: 'Success',
                            okVariant: 'danger'
                        });
                    }

                    this.moveMode = 0;
                    this.hideEditor();
                }).catch(err => {
                this.moveMode = 0;
                this.hideEditor();
            });
        },

        toolForGroup(titleGroup) {
            for (const marking of titleGroup) {
                const toolId = marking.project_marking_tool_id || (marking.tool && marking.tool.id);
                if (toolId) {
                    const liveTool = this.projectTools.find(t => t.id == toolId);
                    if (liveTool) return liveTool;
                }

                if (marking.tool && marking.tool.id) {
                    return marking.tool;
                }
            }
            return null;
        },

        openColorPicker(tool) {
            if (!this.canEditMarkings) {
                return;
            }

            this.colorPickerTool = tool;
            this.pendingColor = null;
            this.pendingColorSelected = false;
            this.$bvModal.show('toolColorModal');
        },

        async confirmBulkColor(color) {
            if (!this.canEditMarkings) {
                return;
            }

            if (!this.colorPickerTool) return;
            const count = this.markings.filter(
                m => m.project_marking_tool_id === this.colorPickerTool.id && !m.deleted_at
            ).length;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `This will update the color of ${count} marking${count !== 1 ? 's' : ''} in this layout. You can reverse this action by changing the color of individual markings`,
                {
                    title: 'Apply bulk color change?',
                    okTitle: 'Apply',
                    okVariant: 'danger',
                    cancelTitle: 'Cancel',
                    centered: true,
                }
            );
            if (confirmed) {
                this.applyBulkColor(color);
            }
        },

        async applyBulkColor(color) {
            if (!this.activeFile || !this.colorPickerTool || this.isBulkUpdatingColor) return;

            this.isBulkUpdatingColor = true;
            const token = this.$store.state.token;

            try {
                await axios({
                    url: this.$store.getters.apiProjectBase + '/markings/bulk-update-color',
                    method: 'POST',
                    data: {
                        project_marking_tool_id: this.colorPickerTool.id,
                        catalog_1: color,
                        file_id: this.activeFile.id,
                    },
                    headers: { Authorization: 'Bearer ' + token },
                });

                const toolId = this.colorPickerTool.id;
                this.markings.forEach(marking => {
                    if (marking.project_marking_tool_id === toolId && !marking.deleted_at) {
                        marking.catalog_1 = color;
                    }
                });

                this.$bvModal.hide('toolColorModal');
                this._computedWatchers.activeMarkings.run();
                this.$forceUpdate();
            } catch (err) {
                console.error('Bulk color update failed:', err);
            } finally {
                this.isBulkUpdatingColor = false;
            }
        },

        cancelEditMarking() {
            this.hideEditor();
        },

        temporaryMarkingChange(marking) {
            this.marking = marking;
        },

        print() {
            this.isPrinting = true;
            const that = this;

            setTimeout(function () {
                html2canvas(document.querySelector("#pdfPages"), {
                    useCORS: true,
                    scale: 2,
                }).then(function (canvas) {
                    that.isPrinting = false;

                    let aDownloadLink = document.createElement('a');
                    // Add the name of the file to the link
                    aDownloadLink.download = that.activeFile.name + '_page_' + that.activePage + '.png';
                    // Attach the data to the link
                    aDownloadLink.href = canvas.toDataURL("image/png");
                    // Get the code to click the download link
                    aDownloadLink.click();
                });
            }, 5000);
        },

        excelExport() {
            this.isExcelExporting = true;
            let token = this.$store.state.token;
            let url = this.$store.getters.apiProjectBase + '/markings/' + this.activeFile.id + '/excel';
            let method = 'GET';

            return axios({
                url: url,
                method: method,
                headers: {"Authorization": "Bearer " + token},
                responseType: 'blob', // important
            }).then(response => {
                // Create a file link in the browser's memory
                const href = URL.createObjectURL(response.data);

                // Create "a" HTML element with href to file & click
                const link = document.createElement('a');
                link.href = href;

                // Get the filename from response headers or use a default name
                const contentDisposition = response.headers['content-disposition'];
                const filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'file.xlsx';
                link.setAttribute('download', filename);

                document.body.appendChild(link);
                link.click();

                // Clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);

                this.isExcelExporting = false;
            }).catch(e => {
                console.error('Exception: ', e);
                this.isExcelExporting = false;

                this.$bvModal.msgBoxOk('There was an error while generating the export. Please try again.', {
                    title: 'Error while generating export',
                    okVariant: 'danger'
                });
            });
        },
        hasUserUnlockedMarkings(user) {
            return this.activeMarkings.some(marking => marking.created_by.id === user.id && marking.is_locked);
        },
        hasUserLockedMarkings(user) {
            return this.activeMarkings.some(marking => marking.created_by.id === user.id && !marking.is_locked);
        }
    }
}
</script>

<style scoped>

::v-deep .moveArrow {
    position: absolute;
    display: block;
    width: calc(var(--knob-size) * var(--knob-scale));
    height: calc(var(--knob-size) * var(--knob-scale));
    border-radius: 100%;
    border-style: solid;
    border-width: calc(var(--knob-border) * var(--knob-scale));
    border-color: #ffffff;
    outline: #ff0000 solid calc(var(--knob-outline) * var(--knob-scale));
    background: #ff0000;
    z-index: 9999;
    pointer-events: auto;
    transition: background-color 0.1s linear, border-color 0.1s linear, outline-color 0.1s linear;
}

::v-deep .moveArrow:hover {
    outline-color: rgba(255, 0, 0, 1);
    background: rgba(255, 255, 255, 0.0);
    border-color: rgba(0, 0, 0, 0);
}
/* Hide native checkbox */
.checkbox-label input[type="checkbox"] {
    display: none;
}

/* Custom grey box */
.checkbox-label .custom-checkbox {
    width: 16px;
    height: 16px;
    border: 2px solid #ccc; /* grey box */
    display: inline-block;
    margin-left: 8px; /* spacing from text */
    position: relative;
    border-radius: 3px;
    background-color: #fff;
}

/* Red check when checked */
.checkbox-label input[type="checkbox"]:checked + .custom-checkbox::after {
    content: "";
    position: absolute;
    left: 3px;
    top: 0px;
    width: 5px;
    height: 10px;
    border: solid #dc2626;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}


.menu {
    list-style: none;
    padding: 0;
    user-select: none;
    flex-grow: 1;
}

.menu li {
    padding: 6px 10px 6px 26px;
    cursor: pointer;
    position: relative;
    font-weight: normal;
}


/* ACTIVE ITEM */
.menu li.active {
    font-weight: bold;
    color: #dc2626;
}

.menu-disabled {
    color: #888888;
    pointer-events: none;
}


.menu li.active::before {
    content: "▶";
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #dc2626;
}

.menu-border-right {
    border-right: 2px solid #FF0000;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.parent {
    font-weight: 600;
}

.submenu {
    list-style: none;
    padding-left: 20px;
}

.indent {
    padding-left: 30px;
}

.checkbox {
    cursor: default;
}

.image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    cursor: zoom-in;
    overflow: hidden;
}

.image-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.image-wrapper:hover .image-overlay {
    opacity: 1;
}

.overlay-icon {
    color: #fff;
    font-size: 3.5rem;
}


.attachment-card {
    width: 100%;
    max-width: 300px;
    height: 250px;
}

.attachment-description {
    bottom: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.7);
    padding: 2px 5px;
    font-size: 0.85rem;
}

.attachment-circle {
    top: 10px;
    right: 10px;
    z-index: 10;
    width: 36px;
    height: 36px;
    color: #fff;
}

.attachment-circle:hover {
    background-color: #dc2626;
    border-color: #dc2626;
}

.danger {
    color: #dc2626;
}

.marking-signal {
    border: 50px solid rgb(255, 0, 0);
}

.upload-card {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    cursor: pointer;
    background-color: #fff;
    color: #888;;
}

.upload-placeholder {
    font-size: 0.9rem;
    font-weight: bold;
}

.upload-card:hover {
    transition: all 0.3s ease-in-out;
    background-color: #f6f6f6;
    color: #dc2626;
}

.btn-disabled {
    color: #fff;
    background-color: #eee;
    pointer-events: none;
}

.align-right {
    text-align: right;
    width: 100%;
}

.marking-deleted {
    opacity: 0.4;
}

::v-deep .marking-highlighted {
}

::v-deep .marking-highlighted::after {
    content: '';
    position: absolute;

    width: 100%;
    height: 100%;

    left: 0;
    top: 0;


    background: rgba(226, 6, 19, 0.6);
    border-radius: 50%;
    outline: 1px solid rgba(226, 6, 19, 0.6);

    animation: ripple 2.5s infinite;

    pointer-events: none;
    z-index: 9999;
}

@keyframes ripple {
    0% {
        outline-width: 10px;
        outline-color: rgba(226, 6, 19, 0.6);
        background: rgba(226, 6, 19, 0.6);
        transform: scale(0.5);
    }
    70% {
        outline-color: rgba(226, 6, 19, 0.3);
        background: rgba(226, 6, 19, 0.3);
    }
    100% {
        outline-width: 50px;
        outline-color: rgba(226, 6, 19, 0);
        background: rgba(226, 6, 19, 0);
        transform: scale(1);
    }
}

.marking-page-icon {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    color: #555555;
}

.marking-page-number {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -55%);
    font-size: 0.6rem;
    color: #000;
   /* color: #dc2626;*/
    pointer-events: none;
}

.markings {
    hr.separator {
        margin: 0;
    }

    .markings-inner {
        .card-header {
            margin-bottom: 5px;
        }

        .marking-row {
            font-size: .9rem;
            border-radius: 3px;

            .marking-actions {
                opacity: .2;
                position: absolute;
                right: 5px;
                padding: 0 2px;
                background-color: #fff;
                border-radius: 3px;
                transition: all .2s;
            }

            &:hover {
                .marking-actions {
                    display: initial;
                    opacity: .5;

                    &:hover {
                        opacity: 1;
                    }
                }
            }
        }
    }
}

.markings-sort-select {
    max-width: 130px;
    padding: 0 5px;
    font-size: 13px;
    color: #343a40;
    background-color: #ffffff;
    border: 1px solid #dcdfe3;
    border-radius: 6px;
    outline: none;
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;

    &:focus {
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
    }

    &:hover:not(:focus) {
        border-color: #c3c7cf;
    }
}

.tool-color-icon-btn {
    padding: 2px 6px;
    color: #888;
    line-height: 1;
    flex-shrink: 0;

    &:hover {
        color: #dc2626;
    }
}

.bulk-color-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0;
}

.bulk-color-swatch {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid rgba(0,0,0,0.12);
    transition: transform 0.15s, border-color 0.15s;

    &:hover {
        transform: scale(1.2);
        border-color: rgba(0,0,0,0.4);
    }

    &.bulk-color-swatch--selected {
        transform: scale(1.2);
        border: 2px solid #dc2626;
    }

    &--unset {
        border-style: dashed;
    }
}

.report-color-swatch {
    display: inline-block;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    border: 2px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;
}

.report-modal-type-item {
    border-radius: 3px;
    padding: 6px 4px;
    cursor: pointer;

    &:hover {
        background-color: #F9CCCE;
    }

    &::v-deep .custom-control-input:checked ~ .custom-control-label::before {
        border-color: #E20613;
        background-color: #E20613;
    }
}

.report-type-icon {
    display: inline-block;
    width: 17px;
    height: 17px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 6px;
    flex-shrink: 0;
}

.report-type-label {
    font-size: 0.85rem;
}

.report-modal-tool-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-right: 6px;
}

::v-deep .report-date-radios .custom-radio .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #E20613;
    background-color: #E20613;
}
::v-deep .report-date-radios .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
    background-image: none;
}

.type-icon--camera   { background-image: url(~@images/marking-icon-camera_new.svg); }
.type-icon--note     { background-image: url(~@images/marking-icon-note_new.svg); }
.type-icon--measure  { background-image: url(~@images/marking-icon-measure_new.svg); }
.type-icon--gallery  { background-image: url(~@images/marking-icon-gallery_new.svg); }
.type-icon--draw     { background-image: url(~@images/marking-icon-draw_new.svg); }
.type-icon--checkbox { background-image: url(~@images/marking-icon-checkbox-unchecked_new.svg); }
.type-icon--firestop { background-image: url(~@images/marking-icon-firestop_new.svg); }
.type-icon--custom   { background-image: url(~@images/marking-icon-custom_new.svg); }
</style>
